import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { observer } from "mobx-react";
import { Box, Typography, useTheme } from "@mui/material";

import { useChatCategoryStore } from "../../../hooks/useChatCategoryStore";
import { useAppStore } from "../../../hooks/useAppStore";
import { useChatStore } from "../../../hooks/useChatStore";
import CoreVirtualizedTable from "../../ui/core/CoreVirtualizedTable";
import Row from "../../ui/core/Row";
import ContractFileTypeIcon from "../../icons/ContractFileTypeIcon";
import { IContractPreviewItem } from "../../../types/chat";
import {
  ColumnFiltersConfig,
  ColumnFilterType,
  SimpleColumnDef,
  SortingState,
} from "../../../types/table";
import { LOB_VALUE_TO_NAME, STATE_CODE_TO_NAME } from "../../../config";

// Columns that get an Excel-like header filter popover. Unlike the earlier client-side
// version of this, these filters are backend-driven (ChatCategoryStore.setColumnFilter /
// clearColumnFilter -> fetchContracts) -- they narrow the whole result set, the same way
// sorting and the search box already do, not just whatever page is currently loaded.
const FILTERABLE_COLUMNS = ["title", "state", "payer", "line_of_business", "effective_date"];

// title/payer are free text; state/line_of_business default to list; effective_date is a date range
const COLUMN_FILTER_TYPES: Record<string, ColumnFilterType> = {
  title: "text",
  payer: "text",
  effective_date: "dateRange",
};

export const ContractResearchTable = observer(() => {
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const chatStore = useChatStore();
  const { setContractToPreview, contractToPreview } = useAppStore();

  const {
    isLoadingContracts,
    contractsData,
    isLoadingMore,
    hasMoreData,
    fetchContracts,
    contractsSort,
    setContractsSorting,
    columnFilters,
    setColumnFilter,
    clearColumnFilter,
  } = useChatCategoryStore();

  const [tableHeight, setTableHeight] = useState<number>(500);

  // Sorting is backend-driven: reflect the store's sort state and ask it to refetch on change
  const sorting: SortingState = useMemo(
    () =>
      contractsSort.by
        ? [{ id: contractsSort.by, desc: contractsSort.direction === "desc" }]
        : [],
    [contractsSort],
  );

  const handleSortingChange = useCallback(
    (newSorting: SortingState) => {
      const [sortConfig] = newSorting;
      setContractsSorting(sortConfig?.id ?? "", sortConfig?.desc ? "desc" : "asc");
    },
    [setContractsSorting],
  );

  const calculateHeight = useCallback(() => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const bottomPadding = 12;
      const calculatedHeight = viewportHeight - rect.top - bottomPadding;
      setTableHeight(Math.max(300, calculatedHeight));
    }
  }, []);

  useEffect(() => {
    calculateHeight();
    window.addEventListener("resize", calculateHeight);
    return () => window.removeEventListener("resize", calculateHeight);
  }, [calculateHeight]);


  // Load the next page once the user scrolls near the bottom of the table
  const handleLoadMore = useCallback(() => {
    fetchContracts(true);
  }, [fetchContracts]);

  // Human-readable label for a filter option value -- state/line_of_business are stored
  // (and sent to the backend) as raw codes, but should still display their full name.
  const getOptionLabel = useCallback((columnId: string, value: string): string => {
    if (columnId === "state") return STATE_CODE_TO_NAME[value] || value;
    if (columnId === "line_of_business") return LOB_VALUE_TO_NAME[value] || value;
    return value;
  }, []);

  // Options for a column's filter popover. state/line_of_business use the full canonical
  // universe of codes (not just what's in the currently-loaded page) since a backend-driven
  // filter can match rows that haven't been fetched yet. title/payer are freeSolo text, so
  // there's no fixed universe -- these are just autocomplete suggestions from loaded rows.
  const getUniqueColumnValues = useCallback(
    (columnId: string): string[] => {
      if (columnId === "state") {
        return Object.keys(STATE_CODE_TO_NAME).sort((a, b) =>
          STATE_CODE_TO_NAME[a].localeCompare(STATE_CODE_TO_NAME[b]),
        );
      }
      if (columnId === "line_of_business") {
        return Object.keys(LOB_VALUE_TO_NAME).sort((a, b) =>
          LOB_VALUE_TO_NAME[a].localeCompare(LOB_VALUE_TO_NAME[b]),
        );
      }

      const values = (contractsData.contracts || [])
        .map((contract) => (contract as unknown as Record<string, unknown>)[columnId])
        .filter((value): value is string => typeof value === "string" && value !== "");
      return Array.from(new Set(values)).sort();
    },
    [contractsData.contracts],
  );

  const columnFiltersConfig: ColumnFiltersConfig = useMemo(
    () => ({
      filters: columnFilters,
      getOptions: getUniqueColumnValues,
      getOptionLabel,
      onApply: setColumnFilter,
      onClear: clearColumnFilter,
      filterableColumns: FILTERABLE_COLUMNS,
      columnFilterTypes: COLUMN_FILTER_TYPES,
    }),
    [columnFilters, getUniqueColumnValues, getOptionLabel, setColumnFilter, clearColumnFilter],
  );

  const handleRowClick = (contract: IContractPreviewItem) => {
    const isSelected = contractToPreview?.document_id === contract.document_id;

    setContractToPreview(isSelected ? null : contract);
    chatStore.setPreviewFileChanged(true);
  };

  const columns: SimpleColumnDef<IContractPreviewItem>[] = useMemo(
    () => [
      {
        id: "title",
        header: "Contract Name",
        accessorKey: "title",
        width: 320,
        minWidth: 280,
        cell: (value: string) => (
          <Row sx={{ alignItems: "center", gap: 1.5, minWidth: 0 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexShrink: 0,
                color: "text.secondary",
                paddingLeft: "20px"
              }}
            >
              <ContractFileTypeIcon fileName={value} />
            </Box>
            <Typography noWrap title={value}>
              {value}
            </Typography>
          </Row>
        ),
      },
      {
        id: "state",
        header: "State",
        accessorKey: "state",
        width: 140,
        minWidth: 120,
        cell: (value?: string) => (
          <Typography
            noWrap
            title={value ? STATE_CODE_TO_NAME[value] || value : undefined}
          >
            {value ? STATE_CODE_TO_NAME[value] || value : "—"}
          </Typography>
        ),
      },
      {
        id: "payer",
        header: "Plan Name",
        accessorKey: "payer",
        width: 200,
        minWidth: 180,
        cell: (value?: string) => (
          <Typography noWrap title={value}>
            {value || "—"}
          </Typography>
        ),
      },
      {
        id: "line_of_business",
        header: "Line of Business",
        accessorKey: "line_of_business",
        width: 200,
        minWidth: 180,
        cell: (value?: string) => (
          <Typography
            noWrap
            title={value ? LOB_VALUE_TO_NAME[value] || value : undefined}
          >
            {value ? LOB_VALUE_TO_NAME[value] || value : "—"}
          </Typography>
        ),
      },
      {
        id: "effective_date",
        header: "Effective Date",
        accessorKey: "effective_date",
        width: 160,
        minWidth: 140,
        cell: (value?: string) => (
          <Typography noWrap>{value || "—"}</Typography>
        ),
      },
    ],
    [],
  );

  return (
    <div
      ref={containerRef}
      style={{
        overflowX: "auto",
        background: "white",
        marginLeft: "12px",
        marginRight: "12px",
        marginBottom: "12px",
        borderRadius: "8px",
        border: "1px solid",
        borderColor: theme.palette.divider,
        width: "calc(100% - 24px)",
      }}
    >
      <CoreVirtualizedTable<IContractPreviewItem>
        isLoading={isLoadingContracts}
        data={contractsData.contracts}
        columns={columns}
        sorting={sorting}
        onSortingChange={handleSortingChange}
        height={`${tableHeight}px`}
        minWidth={500}
        onRowClick={handleRowClick}
        getRowId={(row) => row.document_id}
        getRowStyle={() => ({})}
        emptyMessage="No contracts found"
        onLoadMore={handleLoadMore}
        hasMore={hasMoreData}
        isLoadingMore={isLoadingMore}
        selectedRowId={contractToPreview?.document_id}
        columnFilters={columnFiltersConfig}
        disableLastOfTypeStyles
        isResizable
      />
    </div>
  );
});
